import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { getBlob, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { auth, db, storage } from '../../../firebase';

const COLLECTION_NAME = 'media_packages';

const compactHash = (value) => {
  const source = String(value || '');
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const safeSegment = (value, fallback = 'guide') => String(value || fallback)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-zA-Z0-9_-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80) || fallback;

export const mediaPackageWorkspaceId = (guideKey) => (
  `${safeSegment(guideKey)}_${compactHash(guideKey)}`
);

export const getMediaPackageStorageDownloadUrl = async (storagePath) => {
  if (!storagePath) return '';
  return getDownloadURL(ref(storage, storagePath));
};

export const getMediaPackageStorageBlob = async (storagePath) => {
  if (!storagePath) throw new Error('A Media Package storage path is required.');
  return getBlob(ref(storage, storagePath));
};

export const loadMediaPackageLayoutPreference = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to load Media Package layout defaults.');
  const snapshot = await getDoc(doc(db, 'media_package_preferences', user.uid));
  return snapshot.exists() ? snapshot.data()?.defaultLayout || null : null;
};

export const loadLatestReusablePhoneMockup = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to load the reusable phone mockup.');
  const snapshots = await getDocs(query(
    collection(db, COLLECTION_NAME),
    orderBy('updatedAt', 'desc'),
    limit(30)
  ));
  for (const snapshot of snapshots.docs) {
    const data = snapshot.data() || {};
    if (data.updatedBy && data.updatedBy !== user.uid) continue;
    const config = data.packageData?.config || {};
    const frameUrl = data.assets?.phoneFrame?.url || config.phone_frame_url || '';
    if (frameUrl) {
      return {
        config: { ...config, phone_frame_url: frameUrl },
        sourceWorkspaceId: snapshot.id,
      };
    }
  }
  return null;
};

export const saveMediaPackageLayoutPreference = async (layout) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to save Media Package layout defaults.');
  await setDoc(doc(db, 'media_package_preferences', user.uid), {
    defaultLayout: jsonSafe(layout),
    updatedAt: serverTimestamp(),
    updatedByEmail: user.email || '',
  }, { merge: true });
};

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  if (!response.ok) throw new Error('A local Media Package image could not be prepared for cloud storage.');
  return response.blob();
};

const extensionForBlob = (blob, fallback) => {
  const named = String(blob?.name || '').match(/\.([a-zA-Z0-9]{2,5})$/)?.[1];
  if (named) return named.toLowerCase();
  const mime = String(blob?.type || '').split('/')[1]?.split(';')[0];
  if (mime === 'jpeg') return 'jpg';
  if (mime === 'quicktime') return 'mov';
  return safeSegment(mime, fallback);
};

const fileFingerprint = (file, prefix) => (
  `${prefix}:${file?.name || ''}:${file?.size || 0}:${file?.lastModified || 0}:${file?.type || ''}`
);

const dataUrlFingerprint = (value, prefix) => (
  `${prefix}:${String(value || '').length}:${compactHash(value)}`
);

const uploadAsset = ({ workspaceId, assetName, blob, fingerprint, onProgress }) => new Promise((resolve, reject) => {
  const extension = extensionForBlob(blob, assetName === 'story_media' ? 'mp4' : 'jpg');
  const storagePath = `media_packages/${workspaceId}/assets/${assetName}.${extension}`;
  const uploadRef = ref(storage, storagePath);
  const task = uploadBytesResumable(uploadRef, blob, {
    contentType: blob.type || 'application/octet-stream',
    customMetadata: {
      mediaPackageWorkspace: workspaceId,
      uploadedBy: auth.currentUser?.uid || '',
    },
  });
  task.on('state_changed', (snapshot) => {
    const percent = snapshot.totalBytes
      ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
      : 0;
    onProgress?.(assetName, percent);
  }, reject, async () => {
    try {
      resolve({
        storagePath,
        url: await getDownloadURL(task.snapshot.ref),
        contentType: blob.type || 'application/octet-stream',
        size: blob.size || 0,
        fingerprint,
      });
    } catch (error) {
      reject(error);
    }
  });
});

export const uploadMediaPackageStoryAsset = ({ workspaceId, storyId, file, assetSlot = 'source', onProgress }) => {
  const user = auth.currentUser;
  if (!user) return Promise.reject(new Error('Sign in to upload Story media.'));
  const safeStoryId = safeSegment(storyId, `story_${Date.now()}`);
  const safeAssetSlot = ['source', 'image_overlay'].includes(assetSlot) ? assetSlot : 'source';
  const fingerprint = fileFingerprint(file, `story:${safeStoryId}:${safeAssetSlot}`);
  const extension = extensionForBlob(file, file.type?.startsWith('video/') ? 'mp4' : 'jpg');
  const storagePath = `media_packages/${safeSegment(workspaceId)}/stories/${safeStoryId}/${safeAssetSlot}.${extension}`;
  const uploadRef = ref(storage, storagePath);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(uploadRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        mediaPackageWorkspace: workspaceId,
        storyId: safeStoryId,
        uploadedBy: user.uid,
      },
    });
    task.on('state_changed', (snapshot) => {
      const percent = snapshot.totalBytes
        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        : 0;
      onProgress?.(percent);
    }, reject, async () => {
      try {
        resolve({
          storagePath,
          url: await getDownloadURL(task.snapshot.ref),
          contentType: file.type || 'application/octet-stream',
          size: file.size || 0,
          fingerprint,
        });
      } catch (error) {
        reject(error);
      }
    });
  });
};

const jsonSafe = (value) => JSON.parse(JSON.stringify(value));

export const loadMediaPackageCloudDraft = async (guideKey) => {
  if (!auth.currentUser) throw new Error('Sign in to load the shared Media Package.');
  const workspaceId = mediaPackageWorkspaceId(guideKey);
  const snapshot = await getDoc(doc(db, COLLECTION_NAME, workspaceId));
  if (!snapshot.exists()) return null;
  const data = snapshot.data() || {};
  return {
    workspaceId,
    packageData: data.packageData || null,
    assets: data.assets || {},
    updatedByEmail: data.updatedByEmail || '',
    updatedAt: data.updatedAt || null,
    published: data.published || null,
  };
};

export const publishMediaPackageCloudSnapshot = async ({
  guideKey,
  packageData,
  assets = {},
  generatedAssets = {},
  zipBlob,
  warnings = [],
  onProgress,
}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to publish the client Media Package.');
  if (!(zipBlob instanceof Blob)) throw new Error('The Media Package ZIP could not be prepared.');
  const workspaceId = mediaPackageWorkspaceId(guideKey);
  const documentRef = doc(db, COLLECTION_NAME, workspaceId);
  const currentSnapshot = await getDoc(documentRef);
  const currentVersion = Number(currentSnapshot.data()?.published?.version) || 0;
  const version = currentVersion + 1;
  const publishedPackageData = jsonSafe(packageData);
  const publishedStories = Array.isArray(publishedPackageData?.config?.instagram_story_series)
    ? publishedPackageData.config.instagram_story_series
    : [];
  for (const story of publishedStories) {
    if (!story.rendered_video_url && story.rendered_video_storage_path) {
      try {
        story.rendered_video_url = await getDownloadURL(ref(storage, story.rendered_video_storage_path));
      } catch {
        // The ZIP builder reports inaccessible optional files; keep publishing the remaining package.
      }
    }
  }
  if (!publishedPackageData?.config?.instagram_story_last_render_url
    && publishedPackageData?.config?.instagram_story_last_render_storage_path) {
    try {
      publishedPackageData.config.instagram_story_last_render_url = await getDownloadURL(
        ref(storage, publishedPackageData.config.instagram_story_last_render_storage_path)
      );
    } catch {
      // Keep the published snapshot usable even when an optional legacy render is unavailable.
    }
  }
  const publishedAssets = { ...jsonSafe(assets), generated: {} };
  const previewEntries = Object.entries(generatedAssets || {}).filter(([, blob]) => blob instanceof Blob);
  for (let index = 0; index < previewEntries.length; index += 1) {
    const [assetId, blob] = previewEntries[index];
    const previewStoragePath = `media_packages/${workspaceId}/published/v${version}/previews/${safeSegment(assetId, `asset_${index + 1}`)}.png`;
    const previewRef = ref(storage, previewStoragePath);
    const uploadedPreview = await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(previewRef, blob, {
        contentType: blob.type || 'image/png',
        customMetadata: {
          mediaPackageWorkspace: workspaceId,
          version: String(version),
          assetId,
          uploadedBy: user.uid,
        },
      });
      task.on('state_changed', undefined, reject, () => resolve(task.snapshot.ref));
    });
    publishedAssets.generated[assetId] = {
      url: await getDownloadURL(uploadedPreview),
      storagePath: previewStoragePath,
      contentType: blob.type || 'image/png',
      size: blob.size || 0,
    };
    onProgress?.(Math.round(((index + 1) / Math.max(1, previewEntries.length)) * 35));
  }
  const storagePath = `media_packages/${workspaceId}/published/v${version}/media_package.zip`;
  const uploadRef = ref(storage, storagePath);
  const zip = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(uploadRef, zipBlob, {
      contentType: 'application/zip',
      customMetadata: {
        mediaPackageWorkspace: workspaceId,
        version: String(version),
        uploadedBy: user.uid,
      },
    });
    task.on('state_changed', (snapshot) => {
      const percent = snapshot.totalBytes
        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        : 0;
      onProgress?.(35 + Math.round(percent * 0.65));
    }, reject, async () => resolve({
      url: await getDownloadURL(task.snapshot.ref),
      storagePath,
      size: zipBlob.size || 0,
      contentType: 'application/zip',
    }));
  });
  const publishedAt = new Date().toISOString();
  const published = {
    version,
    packageData: publishedPackageData,
    assets: publishedAssets,
    zip,
    warnings: jsonSafe(warnings),
    publishedAt,
    publishedByEmail: user.email || '',
  };
  await setDoc(documentRef, {
    published,
    publishedAt: serverTimestamp(),
    publishedBy: user.uid,
  }, { merge: true });
  return published;
};

export const saveMediaPackageCloudDraft = async ({
  guideKey,
  packageData,
  localCampaignImage,
  localPartnerLogo,
  localPhoneFrame,
  localPhoneScreen,
  localVideoMedia,
  previousAssets = {},
  onProgress,
}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to save the shared Media Package.');
  const workspaceId = mediaPackageWorkspaceId(guideKey);
  const assets = { ...previousAssets };

  if (String(localCampaignImage || '').startsWith('data:')) {
    const fingerprint = dataUrlFingerprint(localCampaignImage, 'campaign');
    if (assets.campaignImage?.fingerprint !== fingerprint) {
      assets.campaignImage = await uploadAsset({
        workspaceId,
        assetName: 'campaign_image',
        blob: await dataUrlToBlob(localCampaignImage),
        fingerprint,
        onProgress,
      });
    }
  }

  if (String(localPartnerLogo || '').startsWith('data:')) {
    const fingerprint = dataUrlFingerprint(localPartnerLogo, 'partner');
    if (assets.partnerLogo?.fingerprint !== fingerprint) {
      assets.partnerLogo = await uploadAsset({
        workspaceId,
        assetName: 'partner_logo',
        blob: await dataUrlToBlob(localPartnerLogo),
        fingerprint,
        onProgress,
      });
    }
  }

  for (const [assetName, value, key] of [
    ['phone_frame', localPhoneFrame, 'phoneFrame'],
    ['phone_screen', localPhoneScreen, 'phoneScreen'],
  ]) {
    if (String(value || '').startsWith('data:')) {
      const fingerprint = dataUrlFingerprint(value, assetName);
      if (assets[key]?.fingerprint !== fingerprint) {
        assets[key] = await uploadAsset({
          workspaceId,
          assetName,
          blob: await dataUrlToBlob(value),
          fingerprint,
          onProgress,
        });
      }
    }
  }

  if (localVideoMedia instanceof Blob) {
    const fingerprint = fileFingerprint(localVideoMedia, 'story');
    if (assets.storyMedia?.fingerprint !== fingerprint) {
      assets.storyMedia = await uploadAsset({
        workspaceId,
        assetName: 'story_media',
        blob: localVideoMedia,
        fingerprint,
        onProgress,
      });
    }
  }

  const serializedPackage = jsonSafe(packageData);
  const byteSize = new Blob([JSON.stringify(serializedPackage)]).size;
  if (byteSize > 900 * 1024) {
    throw new Error('This Media Package is too large for its Firebase document. Remove unusually long draft content and try again.');
  }

  await setDoc(doc(db, COLLECTION_NAME, workspaceId), {
    schemaVersion: 1,
    guideKey,
    guideId: packageData?.guide?.guide_id || '',
    language: packageData?.config?.output_language || packageData?.guide?.language || '',
    packageData: serializedPackage,
    assets: jsonSafe(assets),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByEmail: user.email || '',
  }, { merge: true });

  return { workspaceId, assets };
};
